import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { CloudUpload, RefreshCw, PlayCircle, StopCircle, AlertTriangle, CheckCircle2, Clock, Image as ImageIcon, HardDrive, Zap } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const token = () => localStorage.getItem("hb_token");

function fmtBytes(n) {
  if (!n) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0, v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 ? 1 : 0)} ${units[i]}`;
}

function fmtDuration(sec) {
  if (sec == null) return "—";
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60), s = sec % 60;
  if (m < 60) return `${m}m ${s}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

async function api(path, opts = {}) {
  const r = await fetch(`${API}/api/admin/media${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token()}`,
      ...(opts.headers || {}),
    },
  });
  if (!r.ok) {
    const t = await r.text();
    throw new Error(t || `HTTP ${r.status}`);
  }
  return r.json();
}

export default function AdminMediaTab() {
  const [stats, setStats] = useState(null);
  const [status, setStatus] = useState(null);
  const [failed, setFailed] = useState([]);
  const [busy, setBusy] = useState(false);
  const poll = useRef(null);

  const load = async () => {
    try {
      const [s, st] = await Promise.all([api("/stats"), api("/warmup/status")]);
      setStats(s);
      setStatus(st);
    } catch (e) { /* silent — likely 403 or CDN unset */ }
  };

  const loadFailed = async () => {
    try {
      const r = await api("/images?status=failed&limit=50");
      setFailed(r.images || []);
    } catch (e) { /* noop */ }
  };

  useEffect(() => {
    load();
    loadFailed();
  }, []);

  // Auto-poll while a job is running
  useEffect(() => {
    if (status?.state === "running") {
      poll.current = setInterval(load, 2500);
      return () => clearInterval(poll.current);
    }
    if (poll.current) { clearInterval(poll.current); poll.current = null; }
  }, [status?.state]);

  const start = async (onlyPending = false) => {
    setBusy(true);
    try {
      await api(`/warmup/start?only_pending=${onlyPending}`, { method: "POST" });
      toast.success(onlyPending ? "Retrying failed images…" : "Warmup started");
      await load();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const cancel = async () => {
    setBusy(true);
    try {
      await api("/warmup/cancel", { method: "POST" });
      toast("Warmup will pause after current batch");
      await load();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const pct = useMemo(() => {
    if (!status || !status.total) return 0;
    return Math.min(100, Math.round((status.processed / status.total) * 100));
  }, [status]);

  if (!stats) {
    return <div className="font-body text-muted-foreground">Loading media stats…</div>;
  }

  const cdnOff = !stats.cdn_configured;

  return (
    <div data-testid="admin-media-tab" className="space-y-8">
      {cdnOff && (
        <div className="border border-champagne bg-blush/25 p-5 rounded-sm flex gap-3">
          <AlertTriangle className="w-5 h-5 text-rosemetal shrink-0 mt-0.5" />
          <div>
            <p className="font-label text-[10px] tracking-widest text-rosemetal mb-1">CDN NOT CONFIGURED</p>
            <p className="font-body text-sm text-ink/80">
              Set <code className="bg-ink/5 px-1.5 py-0.5 rounded">IMAGE_CDN_BASE</code> and
              {" "}<code className="bg-ink/5 px-1.5 py-0.5 rounded">IMAGE_CDN_SECRET</code> in
              {" "}<code className="bg-ink/5 px-1.5 py-0.5 rounded">backend/.env</code>, then restart the backend to enable mirroring.
              See <code className="bg-ink/5 px-1.5 py-0.5 rounded">cloudflare-worker/README.md</code> for deploy steps.
            </p>
          </div>
        </div>
      )}

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Total images" value={stats.total} icon={<ImageIcon className="w-4 h-4" />} testid="kpi-total" />
        <KPI label="Mirrored to R2" value={stats.mirrored} icon={<CheckCircle2 className="w-4 h-4 text-emerald-600" />} testid="kpi-mirrored" tone="success" />
        <KPI label="Pending" value={stats.pending} icon={<Clock className="w-4 h-4 text-amber-600" />} testid="kpi-pending" />
        <KPI label="Failed" value={stats.failed} icon={<AlertTriangle className="w-4 h-4 text-rose-600" />} testid="kpi-failed" tone={stats.failed > 0 ? "danger" : "muted"} />
        <KPI label="Storage used" value={fmtBytes(stats.bytes_stored)} icon={<HardDrive className="w-4 h-4" />} testid="kpi-bytes" />
        <KPI label="R2 hit ratio" value={`${(stats.mirror_ratio * 100).toFixed(1)}%`} icon={<Zap className="w-4 h-4 text-champagne" />} testid="kpi-ratio" />
        <KPI label="Est. bandwidth saved / mo" value={fmtBytes(stats.estimated_monthly_bandwidth_saved_bytes)} icon={<CloudUpload className="w-4 h-4" />} testid="kpi-bw" />
        <KPI label="CDN base" value={stats.cdn_base ? new URL(stats.cdn_base).host : "—"} icon={<HardDrive className="w-4 h-4" />} testid="kpi-base" small />
      </div>

      {/* Warmup panel */}
      <div className="border border-border rounded-sm p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="font-label text-[10px] tracking-widest text-rosemetal">CATALOG WARM-UP</p>
            <h3 className="font-display text-2xl text-ink mt-1">Pre-mirror every product image</h3>
            <p className="font-body text-sm text-muted-foreground mt-1">
              Walks all products, upserts image records, and forces the Worker to write each into R2. Resumable — safe to interrupt.
            </p>
          </div>
          <div className="flex gap-2">
            {status?.state === "running" ? (
              <button
                onClick={cancel}
                disabled={busy}
                data-testid="warmup-cancel-btn"
                className="inline-flex items-center gap-2 border border-rose-500 text-rose-600 px-4 py-2 rounded-sm font-label text-[10px] tracking-widest hover:bg-rose-50 disabled:opacity-60"
              >
                <StopCircle className="w-3.5 h-3.5" /> PAUSE
              </button>
            ) : (
              <>
                <button
                  onClick={() => start(false)}
                  disabled={busy || cdnOff}
                  data-testid="warmup-start-btn"
                  className="inline-flex items-center gap-2 bg-ink text-cream px-4 py-2 rounded-sm font-label text-[10px] tracking-widest hover:bg-rosemetal disabled:opacity-40"
                >
                  <PlayCircle className="w-3.5 h-3.5" /> START WARM-UP
                </button>
                <button
                  onClick={() => start(true)}
                  disabled={busy || cdnOff || stats.failed + stats.pending === 0}
                  data-testid="warmup-retry-btn"
                  className="inline-flex items-center gap-2 border border-ink text-ink px-4 py-2 rounded-sm font-label text-[10px] tracking-widest hover:bg-ink hover:text-cream disabled:opacity-40"
                >
                  <RefreshCw className="w-3.5 h-3.5" /> RETRY FAILED / PENDING
                </button>
              </>
            )}
          </div>
        </div>

        {status && status.state !== "idle" && (
          <div className="mt-6 space-y-3">
            <div className="flex justify-between text-xs font-label tracking-widest">
              <span className="text-ink">STATE · {status.state.toUpperCase()}</span>
              <span className="text-muted-foreground">
                {status.processed}/{status.total} · MIRRORED {status.mirrored} · FAILED {status.failed} · ETA {fmtDuration(status.eta_seconds)}
              </span>
            </div>
            <div className="h-2 bg-cream rounded-full overflow-hidden">
              <div
                data-testid="warmup-progress-bar"
                className="h-full bg-gradient-to-r from-rosemetal to-champagne transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>
            {status.last_url && (
              <p className="font-body text-[11px] text-muted-foreground truncate" data-testid="warmup-last-url">
                Last: {status.last_url}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Failed list */}
      <div className="border border-border rounded-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <p className="font-label text-[10px] tracking-widest text-rosemetal">RECENT FAILURES</p>
          <button
            onClick={loadFailed}
            className="font-label text-[9px] tracking-widest text-muted-foreground hover:text-ink"
            data-testid="failed-refresh"
          >
            REFRESH
          </button>
        </div>
        {failed.length === 0 ? (
          <p className="font-body text-sm text-muted-foreground">No failures — nice.</p>
        ) : (
          <div className="space-y-2 max-h-80 overflow-y-auto" data-testid="failed-list">
            {failed.map((f) => (
              <div key={f.id} className="text-xs border-b border-border pb-2">
                <div className="font-body text-ink truncate" title={f.origin_url}>{f.origin_url}</div>
                <div className="font-label text-[9px] tracking-widest text-rose-600 mt-0.5">
                  {f.attempts} attempts · {f.error?.slice(0, 160)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function KPI({ label, value, icon, testid, tone = "default", small }) {
  const toneCls = {
    default: "border-border",
    success: "border-emerald-500/40 bg-emerald-50/40",
    danger: "border-rose-500/40 bg-rose-50/40",
    muted: "border-border",
  }[tone];
  return (
    <div className={`border rounded-sm p-4 ${toneCls}`} data-testid={testid}>
      <div className="flex items-center gap-2 font-label text-[9px] tracking-widest text-muted-foreground">
        {icon} {label}
      </div>
      <div className={`font-display text-ink mt-1.5 ${small ? "text-lg truncate" : "text-2xl"}`}>{value}</div>
    </div>
  );
}
